import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";
import { useContent, ColumnSettingsProps, ContentProps, Sizes, SortList, FilterList } from "@cofano/cck";
import useUserPreferences from "components/hooks/useUserPreferences";
import { UserTableSettings } from "components/utils/TableUtils";
import { TableId, useSettingsProvider, useTableProvider } from "components/table";
import { usePermission } from "imports/Utils";

type UseFrontendTableProps = {
	id: TableId | string;
	content?: ContentProps;
	defaultSize?: keyof typeof Sizes;
	defaultPageSize?: number;
	defaultFilter?: FilterList;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onCustomFilter?: (property: string) => ((field: any, value: any) => boolean) | undefined;
	paginationEnabled?: boolean;
	paginationSizeOptions?: Array<number>,
	disableParams?: boolean;
	saveForOrganizationEnabled?: boolean;
}

type StateProps = {
	pagination: {
		currentPage: number;
		pageSize: number;
	},
	filters: FilterList;
	sort?: SortList;
}

const useFrontendTable = ({
	id,
	content = [],
	defaultSize,
	defaultPageSize,
	defaultFilter,
	onCustomFilter,
	paginationEnabled = true,
	paginationSizeOptions,
	disableParams = true,
	saveForOrganizationEnabled = true,
}: UseFrontendTableProps) => {

	const tableId = `cckTable_${id}`;

	const { tableSettingsProvider } = useSettingsProvider();
	const savedSettings = tableSettingsProvider.settings(tableId);

	const user = useStoreState(store => store.session.user);
	const saveUserPreferences = useUserPreferences();

	const { openDialog } = useTableProvider();

	const { isAdmin, isCompanyUser } = usePermission();

	const [ searchParams, setSearchParams ] = useSearchParams();

	const getInitialFilters = useCallback(() => {
		if (!disableParams && searchParams.get("filters")) {
			return JSON.parse(searchParams.get("filters") ?? "");
		}
		return defaultFilter ?? [];
	}, []);

	const [ state, setState ] = useState<StateProps>({
		pagination: {
			currentPage: disableParams ? 0 : parseInt(String(searchParams.get("currentPage") ?? 0)),
			pageSize: disableParams
				? (savedSettings?.pageSize || defaultPageSize || 10)
				: parseInt(String(searchParams.get("pageSize") ?? savedSettings?.pageSize ?? defaultPageSize ?? 10)),
		},
		filters: getInitialFilters(),
		sort: !disableParams && searchParams.get("sort") ? JSON.parse(searchParams.get("sort") ?? "") : savedSettings?.sort,
	});

	const stateRef = useRef(state);
	const tableRef = useRef<{ clearSelection?: () => void }>();

	const handleNewFilter = useCallback((newFilter: FilterList) => {
		setState(oldState => ({
			...oldState,
			filters: newFilter,
			pagination: {
				currentPage: 0,
				pageSize: oldState.pagination.pageSize,
			},
		}));
	}, []);

	const useContentProps = useContent({
		content: content as ContentProps,
		pagination: {
			...state.pagination,
			page: state.pagination.currentPage,
			size: state.pagination.pageSize,
			paginationEnabled: paginationEnabled,
		},
		sort: state.sort,
		filter: state.filters,
		onCustomFilter: onCustomFilter,
	});

	const handleParams = () => {
		const currentParams = new URLSearchParams(window.location.search);

		const newPage = String(state.pagination.currentPage);
		const newSize = String(state.pagination.pageSize);
		const newSort = state.sort ? JSON.stringify(state.sort) : null;
		const newFilters = state.filters.length > 0 ? JSON.stringify(state.filters) : null;

		const hasChanged =
			currentParams.get("currentPage") !== newPage ||
			currentParams.get("pageSize") !== newSize ||
			currentParams.get("sort") !== newSort ||
			currentParams.get("filters") !== newFilters;

		if (hasChanged) {
			setSearchParams(
				(prev) => {
					const latestParams = new URLSearchParams(prev);
					latestParams.set("currentPage", newPage);
					latestParams.set("pageSize", newSize);

					if (newSort) {
						latestParams.set("sort", newSort);
					} else {
						latestParams.delete("sort");
					}

					if (newFilters) {
						latestParams.set("filters", newFilters);
					} else {
						latestParams.delete("filters");
					}

					return latestParams;
				},
				{ replace: true },
			);
		}
	};

	useEffect(() => {
		if (!disableParams) {
			handleParams();
		}
	}, [ state.pagination.currentPage, state.pagination.pageSize, state.sort, state.filters ]);

	useEffect(() => {
		stateRef.current = state;
	}, [ state ]);

	return {
		tableProps: {
			id: tableId,
			tableRef: tableRef,
			defaultSize: savedSettings?.size || defaultSize || "NORMAL",

			settingsProvider: {
				settings: (id: string) => {

					const settings = user?.properties.userPreferences?.[id] as UserTableSettings | undefined;

					return {
						size: settings?.size || defaultSize || Sizes.NORMAL,
						columnSettings: settings?.columnSettings || [],
					};
				},
				onSave: (id: string, columnSettings: Array<ColumnSettingsProps>, size: keyof typeof Sizes, pageSize: number, sort?: SortList) => {
					saveUserPreferences({
						preferences: {
							[id]: {
								columnSettings: columnSettings,
								size: size,
								pageSize: pageSize,
								sort: sort,
							},
						},
					});
				},
				onReset: (id: string) => {

					const currentPreferences = user?.properties.userPreferences || {};
					delete currentPreferences[id];

					saveUserPreferences({
						preferences: currentPreferences,
					});
				},
			},

			paginationSettings: {
				paginationEnabled: paginationEnabled,
				page: state.pagination.currentPage,
				size: state.pagination.pageSize,
				goToPage: (newCurrentPage: number, newPageSize: number) => {
					setState(oldState => ({
						...oldState,
						pagination: {
							currentPage: newCurrentPage,
							pageSize: newPageSize,
						},
					}));
				},
				sizeOptions: paginationSizeOptions,
				totalResults: useContentProps.totalResults,
				isLastOnPage: content.length === 1 && (state.pagination.currentPage > 0),
			},

			sortSettings: {
				type: "MULTIPLE" as const,
				sort: state.sort,
				setSort: (newSort: SortList) => {
					setState(oldState => ({
						...oldState,
						sort: newSort,
					}));
				},
			},

			filterSettings: {
				onFilter: (newValues: FilterList) => handleNewFilter(newValues),
				defaultFilter: state.filters,
			},

			content: useContentProps.rows,

			optionalSaveOptions:
				(isAdmin || isCompanyUser) && saveForOrganizationEnabled ?
					[
						{
							label: intl.formatMessage({ id: "SAVE_FOR_ORGANIZATION" }),
							onClick: (_: string, settings: ColumnSettingsProps[]) => openDialog(id, settings),
						},
					]
					:	undefined,
		},

		refetch: (newPage: number) => {

			setState({
				...stateRef.current,
				pagination: {
					currentPage: newPage,
					pageSize: stateRef.current.pagination.pageSize,
				},
			});
		},

		refetchAfterDelete: () => {

			const isLastOnPage = useContentProps.rows.length === 1 && (stateRef.current.pagination.currentPage > 0);

			if (isLastOnPage) {
				setState({
					...stateRef.current,
					pagination: {
						currentPage: stateRef.current.pagination.currentPage - 1,
						pageSize: stateRef.current.pagination.pageSize,
					},
				});
			}
		},

		clearSelection: () => {
			tableRef.current?.clearSelection?.();
		},
	};
};

export default useFrontendTable;
